/**
 * Service Communication - couche métier pour les annonces
 * Utilise le client API typé et normalise les réponses pour le front
 */

import { communicationClient } from '../api'
import type {
  AnnouncementResponse as Announcement,
  ListAnnouncementsQueryDto as AnnouncementQuery,
  CreateAnnouncementDto,
  PaginatedAnnouncementsResponse,
  UpdateAnnouncementDto,
  AnnouncementModuleCapabilities,
  CreateEmailDto,
  EmailResponse,
  ListEmailsQueryDto,
  PaginatedEmailsResponse,
} from '@sphere/communication/domain'

/**
 * Liste paginée des annonces
 */
export async function fetchAnnouncements(
  params: AnnouncementQuery = {}
): Promise<PaginatedAnnouncementsResponse> {
  const response = await communicationClient.announcements.list(params)
  return response.data
}

/**
 * Détail d'une annonce par ID
 */
export async function fetchAnnouncementById(id: string): Promise<Announcement> {
  const response = await communicationClient.announcements.getById(id)
  return response.data
}

/**
 * Création d'une nouvelle annonce
 */
export async function createAnnouncement(
  payload: CreateAnnouncementDto
): Promise<Announcement> {
  const response = await communicationClient.announcements.create(payload)
  return response.data
}

/**
 * Mise à jour d'une annonce
 */
export async function updateAnnouncement(
  id: string,
  payload: UpdateAnnouncementDto
): Promise<Announcement> {
  const response = await communicationClient.announcements.update(id, payload)
  return response.data
}

/**
 * Suppression d'une annonce
 */
export async function deleteAnnouncement(id: string): Promise<void> {
  await communicationClient.announcements.delete(id)
}

/**
 * Marque une annonce comme lue pour l'utilisateur courant
 */
export async function markAnnouncementAsRead(id: string): Promise<void> {
  await communicationClient.announcements.markAsRead(id)
}

/**
 * Nombre d'annonces non lues pour l'utilisateur courant
 */
export async function fetchUnreadAnnouncementCount(): Promise<number> {
  const response = await communicationClient.announcements.unreadCount()
  return response.data.count
}

/**
 * Capabilities globales du module annonces (création, vue admin, etc.)
 */
export async function fetchAnnouncementModuleCapabilities(): Promise<AnnouncementModuleCapabilities> {
  const response = await communicationClient.announcements.capabilities()
  return response.data
}

// ---------------------------------------------------------------------------
// Emails
// ---------------------------------------------------------------------------

/**
 * Liste paginée des emails
 */
export async function fetchEmails(
  params: ListEmailsQueryDto = {}
): Promise<PaginatedEmailsResponse> {
  const response = await communicationClient.emails.list(params)
  return response.data
}

/**
 * Envoi d'un email
 */
export async function sendEmail(
  payload: CreateEmailDto
): Promise<EmailResponse> {
  const response = await communicationClient.emails.send(payload)
  return response.data
}
